import * as React from 'react';

import {
  useBoolean,
  useGetState,
  useLockFn,
  useMemoizedFn,
  useRequest,
  useUpdateEffect,
} from 'ahooks';
import { Button, Divider, List, Modal, notification } from 'antd';

import {
  MenuOutlinedIcon,
  PrimitiveTreeSelect,
  SortAscendingOutlinedIcon,
} from 'components';
import {
  DND_CSS,
  dndArrayMove,
  DndSortableContext,
  useDndSortable,
} from 'context';
import { getCategories, getCategory, updateOrderOfAttributes } from 'services';
import type { VariableAttribute } from 'types';
import { generateTreeSelectData } from 'utils';

type AttributesOrderListItemProps = VariableAttribute;

const AttributesOrderListItem = ({
  id,
  name,
}: AttributesOrderListItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useDndSortable({
    id,
  });

  return (
    <List.Item
      ref={setNodeRef}
      className='bg-slate-50'
      style={{
        transform: DND_CSS.Transform.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
      }}
      {...attributes}
      {...listeners}
    >
      <List.Item.Meta avatar={<MenuOutlinedIcon />} title={name} />
    </List.Item>
  );
};

type AttributesOrderListProps = {
  selectedAttributes: VariableAttribute[];
  selectedCategory: string | undefined;
  handleSelectedAttributes: (attributes: VariableAttribute[]) => void;
};

const AttributesOrderList = React.memo<AttributesOrderListProps>(
  ({
    selectedAttributes,
    selectedCategory,
    handleSelectedAttributes,
  }: AttributesOrderListProps) => {
    const { loading, runAsync } = useRequest(getCategory, {
      manual: true,
    });

    useUpdateEffect(() => {
      if (selectedCategory) {
        runAsync({ id: selectedCategory }).then(({ attributes }) => {
          handleSelectedAttributes(attributes);
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory]);

    return (
      <DndSortableContext
        dnd={{
          onDragEnd: ({ active, over }) => {
            const activeId = active.id;
            const overId = over?.id;

            if (activeId !== overId) {
              const activeIndex = selectedAttributes.findIndex(
                ({ id }) => id === activeId,
              );
              const overIndex = selectedAttributes.findIndex(
                ({ id }) => id === overId,
              );
              const sortedSelectedAttributes = dndArrayMove(
                selectedAttributes,
                activeIndex,
                overIndex,
              );

              handleSelectedAttributes(sortedSelectedAttributes);
            }
          },
        }}
        sortable={{
          items: selectedAttributes.map(({ id }) => id),
          children: (
            <List
              bordered
              size='large'
              className='max-h-[calc(100dvh-25rem)] overflow-y-auto'
              loading={loading}
              dataSource={selectedAttributes}
              renderItem={item => <AttributesOrderListItem {...item} />}
            />
          ),
        }}
      />
    );
  },
);
AttributesOrderList.displayName = 'AttributesOrderList';

type AttributesOrderSelectProps = {
  handleSelectedCategory: (id?: string) => void;
};

const AttributesOrderSelect = React.memo<AttributesOrderSelectProps>(
  ({ handleSelectedCategory }: AttributesOrderSelectProps) => {
    const { data, loading } = useRequest(getCategories, {
      cacheKey: 'categories',
    });

    const totalCategories = data?.categories ?? [];
    const categoriesTreeData = generateTreeSelectData(
      totalCategories,
      'id',
      'title',
      'id',
    );

    return (
      <PrimitiveTreeSelect<string>
        className='w-full'
        placeholder='ابتدا دسته‌بندی مورد نظر را انتخاب کنید'
        loading={loading}
        treeData={categoriesTreeData}
        onChange={value => {
          handleSelectedCategory(value);
        }}
      />
    );
  },
);
AttributesOrderSelect.displayName = 'AttributesOrderSelect';

const AttributesOrder = React.memo(() => {
  const [open, { set: setOpen }] = useBoolean();
  const [confirmLoading, { set: setConfirmLoading }] = useBoolean();
  const [selectedAttributes, setSelectedAttributes] = useGetState<
    VariableAttribute[]
  >([]);
  const [selectedCategory, setSelectedCategory] = useGetState<string>();

  const handleSelectedAttributes = useMemoizedFn(
    (attributes: typeof selectedAttributes) => {
      setSelectedAttributes(attributes);
    },
  );

  const handleSelectedCategory = useMemoizedFn(
    (id: typeof selectedCategory) => {
      setSelectedCategory(id);
    },
  );

  const handleOk = useLockFn(async () => {
    if (selectedCategory && selectedAttributes.length > 0) {
      setConfirmLoading(true);

      updateOrderOfAttributes(
        { categoryId: selectedCategory },
        { attributesIds: selectedAttributes.map(({ id }) => id) },
      )
        .then(() => {
          notification.success({
            message: 'تغییرات ترتیب فیلد‌ها با موفقیت اعمال شد.',
          });
        })
        .catch(message => {
          notification.error({ message });
        })
        .finally(() => {
          setOpen(false);
          setConfirmLoading(false);
        });
    }
  });

  return (
    <>
      <Button
        className='max-lg:basis-full'
        icon={<SortAscendingOutlinedIcon />}
        onClick={() => setOpen(true)}
      >
        ترتیب فیلد‌ها
      </Button>
      <Modal
        centered
        width={600}
        title='به‌روزرسانی ترتیب فیلد‌ها'
        okText='ذخیره تغییرات'
        open={open}
        confirmLoading={confirmLoading}
        onOk={handleOk}
        onCancel={() => setOpen(false)}
      >
        <Divider />
        <div className='space-y-4'>
          <AttributesOrderSelect
            handleSelectedCategory={handleSelectedCategory}
          />
          <AttributesOrderList
            selectedAttributes={selectedAttributes}
            selectedCategory={selectedCategory}
            handleSelectedAttributes={handleSelectedAttributes}
          />
        </div>
        <Divider />
      </Modal>
    </>
  );
});
AttributesOrder.displayName = 'AttributesOrder';

export {
  AttributesOrder,
  AttributesOrderList,
  AttributesOrderListItem,
  AttributesOrderSelect,
};
